/**
 * OnboardingPage — Event-first 3-step onboarding wizard for web.
 *
 * Steps:
 *  1. "Create your first event" — Title + date. That's it.
 *  2. "Add your lineup" — Shows the event just created. InlineEntityInput
 *     to add artists. Skip option.
 *  3. "You're ready!" — Success. "Generate a flyer" opens Editor.
 *     "Go to Dashboard" as secondary.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { InlineEntityInput } from '../components/InlineEntityInput';
import type { Id } from '../../convex/_generated/dataModel';
import {
  CalendarDays,
  ChevronRight,
  ChevronLeft,
  Users,
  PartyPopper,
  Sparkles,
  Loader2,
} from 'lucide-react';

/** Compute the next Saturday at 10 PM. */
function getNextSaturday10pm(): string {
  const now = new Date();
  const day = now.getDay();
  let daysUntil = day === 6 ? 7 : 6 - day;
  if (daysUntil === 0) daysUntil = 7;
  const target = new Date(now);
  target.setDate(target.getDate() + daysUntil);
  target.setHours(22, 0, 0, 0);
  const yyyy = target.getFullYear();
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T22:00`;
}

export function OnboardingPage() {
  const navigate = useNavigate();
  const createEvent = useMutation(api.events.create);
  const updateEvent = useMutation(api.events.update);

  const [step, setStep] = useState(0);
  const [eventTitle, setEventTitle] = useState('');
  const [eventDate, setEventDate] = useState(getNextSaturday10pm);
  const [isCreating, setIsCreating] = useState(false);
  const [createdEventId, setCreatedEventId] = useState<Id<'entities'> | null>(null);
  const [createdEventTitle, setCreatedEventTitle] = useState('');
  const [lineup, setLineup] = useState<string[]>([]);
  const [lineupNames, setLineupNames] = useState<string[]>([]);

  // Step 1: Create Event
  const handleCreateEvent = useCallback(async () => {
    if (!eventTitle.trim()) return;
    setIsCreating(true);
    try {
      const eventId = await createEvent({
        title: eventTitle.trim(),
        date: eventDate ? new Date(eventDate).toISOString() : new Date().toISOString(),
        status: 'confirmed',
        visibility: 'private',
        lineup: [],
        tags: [],
      });
      setCreatedEventId(eventId);
      setCreatedEventTitle(eventTitle.trim());
      setStep(1);
    } catch (err) {
      console.error('Onboarding: Failed to create event:', err);
    } finally {
      setIsCreating(false);
    }
  }, [eventTitle, eventDate, createEvent]);

  // Step 2: Add artist to lineup
  const handleAddToLineup = useCallback(
    async (entity: { _id: string; name: string; entityType: string }) => {
      if (!createdEventId) return;
      if (lineup.includes(entity._id)) return;
      const newLineup = [...lineup, entity._id];
      try {
        await updateEvent({ id: createdEventId, lineup: newLineup });
        setLineup(newLineup);
        setLineupNames((prev) => [...prev, entity.name]);
      } catch (err) {
        console.error('Onboarding: Failed to update lineup:', err);
      }
    },
    [createdEventId, lineup, updateEvent],
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-xl">
        {/* Progress */}
        <div className="flex items-center gap-1 mb-8 justify-center">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i <= step ? 'bg-[#25a48f] w-12' : 'bg-muted w-6'
              }`}
            />
          ))}
        </div>

        {/* Step Content */}
        <div className="rounded-2xl border border-border bg-card p-8">
          {/* Step 0: Create your first event */}
          {step === 0 && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#25a48f]/10 flex items-center justify-center">
                  <CalendarDays className="w-8 h-8 text-[#25a48f]" />
                </div>
                <h1 className="text-2xl font-bold">Create your first event</h1>
                <p className="text-muted-foreground mt-2">
                  What are you working on? Just a name and date is enough.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground block mb-1.5">
                    Event Name
                  </label>
                  <input
                    type="text"
                    value={eventTitle}
                    onChange={(e) => setEventTitle(e.target.value)}
                    placeholder="Saturday Night Live, Bass Night, etc."
                    className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    onKeyDown={(e) =>
                      e.key === 'Enter' && eventTitle.trim() && handleCreateEvent()
                    }
                    autoFocus
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground block mb-1.5">
                    Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={eventDate}
                    onChange={(e) => setEventDate(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 1: Add your lineup */}
          {step === 1 && createdEventId && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-600/10 flex items-center justify-center">
                  <Users className="w-8 h-8 text-purple-600" />
                </div>
                <h1 className="text-2xl font-bold">Add your lineup</h1>
                <p className="text-muted-foreground mt-2">
                  Who's performing at{' '}
                  <span className="font-medium text-foreground">
                    &ldquo;{createdEventTitle}&rdquo;
                  </span>
                  ? Type a name to search or create.
                </p>
              </div>

              {/* Current lineup */}
              {lineupNames.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {lineupNames.map((name, i) => (
                    <span
                      key={i}
                      className="text-sm px-3 py-1 rounded-full bg-[#25a48f]/10 text-[#25a48f] border border-[#25a48f]/20"
                    >
                      {name}
                    </span>
                  ))}
                </div>
              )}

              <InlineEntityInput
                defaultType="dj"
                placeholder="Type an artist name..."
                onSelect={handleAddToLineup}
                excludeIds={lineup}
                filterTypes={[
                  'dj', 'band', 'singer', 'mc', 'performer',
                  'gogo_dancer', 'vj', 'lighting_designer',
                  'performance_artist', 'dancer',
                ]}
              />

              <p className="text-xs text-muted-foreground text-center">
                You can add more artists later from the event workspace.
              </p>
            </div>
          )}

          {/* Step 2: You're ready! */}
          {step === 2 && (
            <div className="space-y-6 text-center">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[#25a48f]/10 flex items-center justify-center">
                <PartyPopper className="w-8 h-8 text-[#25a48f]" />
              </div>
              <h1 className="text-2xl font-bold">You're all set!</h1>
              <p className="text-muted-foreground">
                {createdEventId
                  ? `"${createdEventTitle}" is ready. Generate a flyer or head to your dashboard.`
                  : 'Your scene is ready. Start creating flyers and managing events.'}
              </p>
              <div className="flex flex-col gap-3">
                {createdEventId && (
                  <button
                    onClick={() => navigate('/app/editor')}
                    className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#ef5228] text-white hover:bg-[#d9461f] transition-colors font-medium text-sm"
                  >
                    <Sparkles className="w-4 h-4" />
                    Generate a Flyer
                  </button>
                )}
                <button
                  onClick={() => navigate('/app/dashboard')}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-[#25a48f] text-white hover:bg-[#1e9480] transition-colors font-medium text-sm"
                >
                  Go to Dashboard
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        {step < 2 && (
          <div className="flex items-center justify-between mt-6">
            {step > 0 ? (
              <button
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>
            ) : (
              <div />
            )}
            <button
              onClick={() => {
                if (step === 0) {
                  handleCreateEvent();
                } else {
                  setStep(2);
                }
              }}
              disabled={step === 0 ? !eventTitle.trim() || isCreating : false}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#25a48f] text-white hover:bg-[#1e9480] transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isCreating ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating...
                </>
              ) : step === 0 ? (
                'Create Event'
              ) : lineupNames.length === 0 ? (
                'Skip'
              ) : (
                'Done'
              )}
              {!isCreating && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
